<script setup lang="ts">
import type { BrokerAvatarSequenceProps } from '.';
import type { Agent } from '~/core/entities/agent/Agent';

import { AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn } from '@/utils';
import { getFallbackBrokerImage } from '@/utils/imageUrl';
import { AvatarRoot } from 'reka-ui';

import { avatarVariant } from '.';

const { ORIGIN } = useRuntimeConfig().public;

const FALLBACK_BROKER_IMAGE = getFallbackBrokerImage(ORIGIN);

const props = withDefaults(defineProps<BrokerAvatarSequenceProps>(), {
	avatarFallbackClass: 'bg-white-dark-3 text-base text-white',
	agents: () => []
});

function getAgentAlt(brokerName: Agent['name']) {
	return `Corretor: ${brokerName}`;
}

const getImageSrc = useImage();
const agentsWithExtraData = computed(() =>
	props.agents.map(agent => {
		const profileUrl = agent.profilePicture?.url || '';
		// If agent has a profile picture, use useImage to process it
		// Otherwise, use the fallback image directly
		const imageSrc = profileUrl
			? getImageSrc(profileUrl, { format: 'webp', width: 128 })
			: FALLBACK_BROKER_IMAGE;

		return {
			id: agent.id,
			alt: getAgentAlt(agent.name),
			imageSrc
		};
	})
);
</script>

<template>
	<div :class="cn('relative flex', props.class)">
		<AvatarRoot
			v-for="agent in agentsWithExtraData"
			:key="agent.id"
			:class="cn(avatarVariant(), props.avatarClass)"
		>
			<AvatarImage :src="agent.imageSrc" :alt="agent.alt" />

			<AvatarFallback as-child :class="props.avatarFallbackClass">
				<NuxtImg
					:src="FALLBACK_BROKER_IMAGE"
					width="128"
					height="128"
					loading="lazy"
					format="webp"
					class="size-full rounded-full"
				/>
			</AvatarFallback>
		</AvatarRoot>
	</div>
</template>
